import type {Props} from './types';
import {Box} from '@jobseeker/lotus/foundations';
import {Typography} from '@mui/material';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {CheckCircle, OrangeCheckCircle} from '../../assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {enqueueSnackbar} from 'notistack';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const ProgressBar = (props: Props) => {
  const theme = useWrappedTheme();
  const {
    steps,
    disableFooter,
    showStepCounter = true,
    lockFutureSteps = true,
    useDotsOnly = false,
    dividerColor,
  } = props;
  const isAboveMd = useIsLargeDevice('md');

  let showStepCompleted = true;
  return (
    <Box
      width={{xxs: '100%', md: useDotsOnly ? '100%' : 200}}
      display={'flex'}
      flexDirection={{xxs: useDotsOnly ? 'column' : 'row', md: 'column'}}
      gap={{xxs: 1.625, md: useDotsOnly ? 3 : 4.75}}
    >
      {steps.map((step, index) => {
        if (lockFutureSteps && step.active) {
          showStepCompleted = false;
        }
        const handleClick = () => {
          if (disableFooter) {
            try {
              enqueueSnackbar("Can't switch while recording", {
                variant: 'error',
              });
            } catch {}
          } else if (!step.active && step.link.length) {
            openUrl(step.link, '_self');
          }
        };
        return isAboveMd || step.description ? (
          <Box
            display={{xxs: step.description ? 'flex' : 'none', md: 'flex'}}
            position={'relative'}
          >
            <Box
              position={'inherit'}
              display={'flex'}
              gap={2.5}
              justifyContent={'flex-start'}
              alignItems={
                step.description
                  ? 'flex-start'
                  : step.active
                    ? 'flex-start'
                    : 'center'
              }
              height={step.description ? 'auto' : 24}
              zIndex={10}
              onClick={handleClick}
              sx={{
                cursor: step.active
                  ? 'default'
                  : step.link.length
                    ? 'pointer'
                    : step.description
                      ? 'default'
                      : 'pointer',
              }}
            >
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={20}
                height={20}
                borderRadius={'50%'}
                flexShrink={0}
              >
                {step.description ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={11}
                    height={11}
                    borderRadius="50%"
                    sx={{
                      backgroundColor: dividerColor,
                    }}
                  />
                ) : step.active ? (
                  <Box
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    width={19}
                    height={19}
                    borderRadius={'50%'}
                    sx={{
                      backgroundColor: 'secondary.dark',
                    }}
                  >
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      width={9}
                      height={9}
                      borderRadius={'50%'}
                      sx={{
                        backgroundColor: '#fff',
                      }}
                    />
                  </Box>
                ) : step.description ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={11}
                    height={11}
                    borderRadius="50%"
                    sx={{backgroundColor: '#D9D9D9'}}
                  />
                ) : showStepCompleted ? (
                  <Image
                    src={isAppIimjobs ? CheckCircle : OrangeCheckCircle}
                    width={15}
                    height={15}
                    alt="check"
                  />
                ) : (
                  <Box
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    width={11}
                    height={11}
                    borderRadius={'50%'}
                    sx={{
                      backgroundColor: '#D9D9D9',
                    }}
                  ></Box>
                )}
              </Box>
              <Box display={'flex'} flexDirection={'column'} gap={0.125}>
                <Typography
                  {...(step.description
                    ? {
                        fontSize: '14px',
                        fontWeight: isAppIimjobs ? 600 : 700,
                        color: '#333',
                      }
                    : {
                        variant: 'subtitle3',
                        lineHeight: step.active ? '20px' : '24px',
                        color: step.active
                          ? 'secondary.main'
                          : showStepCompleted
                            ? 'primary.main'
                            : '#33333350',
                      })}
                >
                  {step.label}
                </Typography>
                {step.description && (
                  <Typography
                    component="div"
                    lineHeight={'16px'}
                    fontSize={12}
                    fontStyle="normal"
                    fontWeight={isAppIimjobs ? {xxs: 400, md: 500} : 400}
                    color={'#7E7E7E'}
                    dangerouslySetInnerHTML={{__html: step.description}}
                  />
                )}
                {step.active && showStepCounter && (
                  <Typography
                    color="#33333350"
                    variant="body3"
                    lineHeight={'16px'}
                    fontStyle={'italic'}
                  >{`(Step ${index + 1}/${steps.length})`}</Typography>
                )}
              </Box>
            </Box>
            {index !== steps.length - 1 && (
              <Box
                zIndex={5}
                position={'absolute'}
                width={'1px'}
                height={step.description ? 'auto' : '60px'}
                top={10}
                bottom={{
                  xxs: step.description ? -25 : 0,
                  md: step.description ? -35 : 0,
                }}
                left={10}
                sx={{
                  backgroundColor: dividerColor
                    ? dividerColor
                    : step.active
                      ? 'secondary.main'
                      : showStepCompleted
                        ? 'primary.main'
                        : '#33333350',
                }}
              />
            )}
          </Box>
        ) : (
          <Box
            display={{xxs: 'flex', md: 'none'}}
            height={5}
            width={'100%'}
            borderRadius={12.5}
            onClick={handleClick}
            sx={{
              cursor: step.active
                ? 'default'
                : step.link.length
                  ? 'pointer'
                  : lockFutureSteps
                    ? 'not-allowed'
                    : 'pointer',
              background: step.active
                ? isAppIimjobs
                  ? 'linear-gradient(90deg, #49966E 50%, #E7E7E7 50%);'
                  : `linear-gradient(90deg, ${theme.palette.primary.main} 50%, #E7E7E7 50%);`
                : showStepCompleted
                  ? isAppIimjobs
                    ? '#49966E'
                    : theme.palette.primary.main
                  : '#E7E7E7',
            }}
          />
        );
      })}
    </Box>
  );
};

export default ProgressBar;
